import { Alert, Box, Button, Collapse, Link, Stack, Typography, useMediaQuery } from '@mui/material'
import { ChevronLeft, Sync } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import NominalCodeMappingService from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.service'
import { INominalSummaryResponse, IPostNominalCodeMapping, NominalMappingSource } from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import { normaliseNominalCode } from '../../../../shared/utils/nominal-code-utils'
import { useUnsavedChangesWarning } from '../../../../hooks/use-unsaved-changes-warning'
import { useNominalCodeMapping, useNominalReferenceData } from '../../../../hooks/use-nominal-code-mapping'
import gaiaTheme from '../../../../root-components/theme'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { SettingsMobileNavbar } from '../../../../root-components/navigation/settings-mobile-navbar'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { InformationBox } from '../../../presentation-components/miscellaneous-components/information-box'
import { SelectableCard } from '../../../presentation-components/settings-components/data-import/selectable-card'
import { NominalCodeMappingDialog } from '../../../dialogs/settings/data-import/nominal-code-mapping-dialog'
import { NominalCodeImportDialog } from '../../../dialogs/settings/data-import/nominal-code-import-dialog'
import { NominalCodeMappingWorkspace, NominalCodeSaveBar } from '../../../presentation-components/settings-components/data-import/nominal-code-mapping-workspace'
import { BRAND_DISPLAY_FONT } from '../../signup-login/auth/auth-branding'
import ThirdPartyConnectionDetailsService from '../../../../modules/third-party-connection/shared-services/third-party-connection-details/third-party-connection.service'
import { ACCOUNTING_PROVIDERS } from '../../../../shared/constants/accounting-providers'

// A connected ledger the chart can be read from, with the company that scopes the read. Xero
// needs its tenant id on every call and QuickBooks its company id, and neither is stored on the
// connection row, so it has to be carried from the connection check.
type LedgerChoice = { code: string, name: string, companyId?: string }

const ledgerDisplayName = (code?: string | null): string =>
  LEDGER_PROVIDERS.find(provider => provider.code === code)?.name || 'your ledger'

// The ledgers whose chart of accounts we can read: every accounting integration.
const LEDGER_PROVIDERS: { code: string, name: string }[] = ACCOUNTING_PROVIDERS

/**
 * Chart of Accounts: the one place codes are mapped in bulk.
 *
 * A work queue rather than a list of past decisions. Every code the organisation has mapped or
 * seen (on ledger lines, on spreadsheet spend, or in its connected ledger's chart) comes from one
 * summary call, sorted by the money on it, with the codes still needing a decision shown first.
 * Nothing saves until "Save and apply", and nothing a suggestion proposes saves until accepted.
 *
 * The ledger's chart is stored on the backend, refreshed by every sync and by "Refresh from
 * {ledger}" here, so it is listed on every visit with no URL parameter. Other screens link here
 * instead of owning a copy of the table: the integration page with no parameters, the financial
 * review with ?reportPeriodId=&return=review.
 */
export const ChartOfAccountsPage = () => {
  //Services
  const nominalCodeMappingService = new NominalCodeMappingService()
  const thirdPartyConnectionDetailsService = new ThirdPartyConnectionDetailsService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  // For the stable row callbacks below, which must not hold on to a stale context.
  const userNotificationsRef = useRef(userNotificationsContext)
  userNotificationsRef.current = userNotificationsContext
  const navigate = useNavigate()
  //URL state
  const [searchParams] = useSearchParams()
  const reportPeriodIdParam = searchParams.get('reportPeriodId') || ''
  const returnParam = searchParams.get('return') || ''
  // The company to read from, when the integration page offered a choice (a Business Central
  // tenant can keep a different chart in each company).
  const companyIdParam = searchParams.get('companyId') || ''
  //Summary
  const [summary, setSummary] = useState<INominalSummaryResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState<number>(0)
  const latestRequest = useRef<number>(0)
  //Reference data for the outcome controls, loaded once for every row
  const reference = useNominalReferenceData()
  //Unsaved work, filters, suggestions, the bulk bar and the save: shared with the import step.
  //A stored chart brings every account in the ledger, most never posted to, so they start hidden
  //(the hook shows them anyway when nothing else is left to do), and a period's review only cares
  //about its own lines. With no chart read, every code was seen somewhere, so all are shown.
  const mapping = useNominalCodeMapping({
    summary,
    activityConversions: reference.activityConversions,
    defaultShowNoSpend: !summary?.ledger && !reportPeriodIdParam,
    viewKey: reportPeriodIdParam,
  })
  const { codes, rowsByKey, hasUnsaved, confirmDiscardChanges, removeEdits } = mapping
  //For the stable row callbacks below, which must not hold on to stale rows.
  const rowsByKeyRef = useRef(rowsByKey)
  rowsByKeyRef.current = rowsByKey
  //View
  const [showHowItWorks, setShowHowItWorks] = useState<boolean>(false)
  //Ledger read
  const [ledgerChoices, setLedgerChoices] = useState<LedgerChoice[] | null>(null)
  const [isCheckingConnections, setIsCheckingConnections] = useState<boolean>(false)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  //Add/edit one code, and the spreadsheet import
  const [openMappingDialog, setOpenMappingDialog] = useState<boolean>(false)
  const [editingMapping, setEditingMapping] = useState<{ id: string, key: string } | null>(null)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [errorCode, setErrorCode] = useState<number>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [openImportDialog, setOpenImportDialog] = useState<boolean>(false)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))

  // Close and refresh only. In-app navigation cannot be blocked under the app's BrowserRouter
  // (the hook explains why), so the in-page "Back to review" link confirms through openDialog.
  useUnsavedChangesWarning(hasUnsaved)

  //Use Effects
  //Keyed on the search params, not on mount: arriving with new params while already on the page
  //(a link from the review, or back to the plain page) must reload the summary.
  useEffect(() => {
    const requestId = latestRequest.current + 1
    latestRequest.current = requestId
    setIsLoading(true)
    setLoadError(null)
    nominalCodeMappingService.getSummary({
      reportPeriodId: reportPeriodIdParam || null,
    })
      .then(response => {
        if (requestId !== latestRequest.current) return
        setSummary(response)
        setIsLoading(false)
      })
      .catch(err => {
        if (requestId !== latestRequest.current) return
        console.log(err)
        setIsLoading(false)
        setLoadError(err?.response?.data?.message || 'Your codes could not be loaded. Refresh the page to try again.')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, reloadKey])

  //Handlers
  const reloadSummary = () => setReloadKey(key => key + 1)

  const deleteMappingHandler = useCallback((key: string) => {
    const row = rowsByKeyRef.current.get(key)
    const mappingId = row?.mapping?.id
    if (!row || !mappingId) return
    const notifications = userNotificationsRef.current
    notifications.openDialog({
      active: true,
      title: 'Delete this outcome?',
      message: `Code ${row.nominalCode} will go back to classifying by supplier on future syncs and imports. This cannot be undone.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false,
      have_form_input: false,
    })
      .then(confirmed => {
        if (confirmed !== true) return
        nominalCodeMappingService.delete(mappingId)
          .then(() => {
            notifications.openSnackbar({ open: true, message: 'Deleted successfully' })
            removeEdits([key])
            reloadSummary()
          })
          .catch(err => {
            console.log(err)
            notifications.openSnackbar({ open: true, message: err?.response?.data?.message || 'The outcome could not be deleted. Please try again.', backgroundColor: 'text.primary' })
          })
      })
      .catch(err => console.log(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const editMappingHandler = useCallback((key: string) => {
    const mappingId = rowsByKeyRef.current.get(key)?.mapping?.id
    if (!mappingId) return
    setEditingMapping({ id: mappingId, key })
    setOpenMappingDialog(true)
  }, [])

  const backToReviewHandler = async () => {
    if (!(await confirmDiscardChanges('leave'))) return
    navigate('/emissions/financial')
  }

  const isReviewScope = summary?.scope === 'review' || (!summary && !!reportPeriodIdParam)
  const saveSource: NominalMappingSource = isReviewScope ? 'review' : 'settings'

  const saveHandler = () => mapping.save({
    source: saveSource,
    onSaved: reloadSummary,
  })

  // Which ledgers are actually connected is resolved on click rather than on page load: it costs
  // one request per provider, and most visits to this page are not here to read a ledger. It runs
  // on every click, "Refresh" included, because the check is also what renews an expired token.
  const ledgerReadHandler = () => {
    if (isCheckingConnections || isRefreshing) return
    setIsCheckingConnections(true)
    setRefreshError(null)
    const lastRead = summary?.ledger
    Promise.all(LEDGER_PROVIDERS.map(provider =>
      thirdPartyConnectionDetailsService.checkConnection(provider.code)
        .then(response => {
          const connections = Array.isArray(response?.connections) ? response.connections : []
          // The company that scopes the read: the one chosen on the integration page when this
          // connection can see it, else the one the chart was last read from, else the first
          // company the connection can see, matching what the integration page does when an
          // organisation has more than one.
          const requested = companyIdParam
            ? connections.find((connection: any) => connection.connected && connection.companyId === companyIdParam)
            : undefined
          const previous = lastRead?.provider === provider.code
            ? connections.find((connection: any) => connection.connected && connection.companyId === lastRead.companyId)
            : undefined
          const active = requested || previous || connections.find((connection: any) => connection.connected) || connections[0]
          if (!active) return null
          return { ...provider, companyId: active.companyId }
        })
        .catch(() => null)))
      .then(results => {
        const connected = results.filter(Boolean) as LedgerChoice[]
        setIsCheckingConnections(false)
        if (connected.length === 0) {
          userNotificationsContext.openSnackbar({ open: true, message: 'Connect an accounting integration first, then your chart of accounts can be read from it', backgroundColor: 'text.primary' })
          return
        }
        if (connected.length === 1) {
          setLedgerChoices(null)
          refreshFromLedger(connected[0])
          return
        }
        // Several: the choice is made inline above the table, not in a dialog.
        setLedgerChoices(connected)
      })
      .catch(err => {
        console.log(err)
        setIsCheckingConnections(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'Your connected ledgers could not be checked. Please try again.', backgroundColor: 'text.primary' })
      })
  }

  // Read the chart from the chosen ledger now, store it, then reload the codes. Unsaved edits are
  // kept: each one is keyed by code and carries its own code and description, and a code means
  // the same thing whichever ledger listed it.
  const refreshFromLedger = (choice: LedgerChoice) => {
    if (isRefreshing) return
    setIsRefreshing(true)
    setRefreshError(null)
    nominalCodeMappingService.refreshLedgerAccounts({ provider: choice.code, companyId: choice.companyId || null })
      .then(result => {
        setIsRefreshing(false)
        setLedgerChoices(null)
        const count = result?.accountCount ?? 0
        userNotificationsContext.openSnackbar({ open: true, message: `Read ${count} ${count === 1 ? 'account' : 'accounts'} from ${choice.name}` })
        reloadSummary()
      })
      .catch(err => {
        console.log(err)
        setIsRefreshing(false)
        setRefreshError(err?.response?.data?.message || `Your chart of accounts could not be read from ${choice.name}. Please try again.`)
      })
  }

  //Add or edit one code by hand, through the existing dialog.
  const submitDialogHandler = (values: IPostNominalCodeMapping, resetForm: () => void, isEdit: boolean) => {
    if (isDialogSubmitting) return
    setErrorCode(undefined)
    setErrorMessage(undefined)
    setIsDialogSubmitting(true)
    const request = isEdit && editingMapping
      ? nominalCodeMappingService.edit(editingMapping.id, values)
      : nominalCodeMappingService.create(values)
    request
      .then((response) => {
        // The dialog's save supersedes any unsaved change to the same code on the page.
        removeEdits([normaliseNominalCode(values.nominalCode), editingMapping?.key].filter(Boolean) as string[])
        resetForm()
        closeDialogHandler()
        //Say what the mapping did to lines already in review, rather than only that it saved: a
        //code mapped with no visible effect reads as not having worked.
        userNotificationsContext.openSnackbar({ open: true, message: response?.reapplyMessage || (isEdit ? 'Updated successfully' : 'Saved successfully') })
        setIsDialogSubmitting(false)
        reloadSummary()
      })
      .catch(err => {
        if (err.response?.data?.message) {
          setErrorMessage(err.response.data.message)
          setErrorCode(err.response.data.code)
        } else {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
        }
        console.log(err)
        setIsDialogSubmitting(false)
      })
  }

  const closeDialogHandler = () => {
    setOpenMappingDialog(false)
    setEditingMapping(null)
    setErrorCode(undefined)
    setErrorMessage(undefined)
  }

  //Render helpers
  const lastRead = summary?.ledger || null
  const ledgerName = ledgerDisplayName(lastRead?.provider)
  const lastReadDate = lastRead?.lastReadAt ? moment(lastRead.lastReadAt).format('D MMMM YYYY') : null
  const isEmpty = !isLoading && !loadError && !!summary && codes.length === 0

  const outcomeLabel = (strong: string, rest: string) => (
    <Typography variant='body2' color='text.secondary'>
      <Typography component='span' variant='body2' sx={{ fontFamily: BRAND_DISPLAY_FONT, color: 'text.primary' }}>{strong}</Typography> {rest}
    </Typography>
  )

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <PageHeading
        title='Chart of Accounts'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setEditingMapping(null); setOpenMappingDialog(true) }}
        actionButtonText='Add code'
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => setOpenImportDialog(true)}
        secondaryActionButtonText='Import spreadsheet'
        hasTabs={false}
        extraActions={
          <LoadingButton
            variant='outlined'
            loading={isCheckingConnections || isRefreshing}
            loadingPosition='start'
            startIcon={<Sync fontSize='small' />}
            onClick={ledgerReadHandler}>
            <Typography sx={{ pl: 0.5 }}>{lastRead ? `Refresh from ${ledgerName}` : 'Read from your ledger'}</Typography>
          </LoadingButton>
        }
      />
      {/* One line, always: it is the page's description. The three-outcome explanation sits
          behind "How it works" so a returning user never reads it twice. */}
      <Box sx={{ mb: 2 }}>
          {lastReadDate && (
            <Typography variant='caption' color='text.secondary' component='p' sx={{ mb: 0.5 }}>
              Chart last read from {ledgerName} on {lastReadDate}
            </Typography>
          )}
          <Typography variant='body2' color='text.secondary'>
            Give each code an outcome once; every sync and import reuses it. Unmapped codes keep classifying by supplier.{' '}
            <Link
              component='button'
              type='button'
              variant='body2'
              onClick={() => setShowHowItWorks(open => !open)}
              aria-expanded={showHowItWorks}
              sx={{ verticalAlign: 'baseline' }}>
              {showHowItWorks ? 'Hide how it works' : 'How it works'}
            </Link>
          </Typography>
          <Collapse in={showHowItWorks} unmountOnExit>
            <Stack spacing={1} sx={{ pt: 1 }}>
              {outcomeLabel('Categorise:', 'book the code against a spend activity, so its spend becomes an emission using that activity\'s factor.')}
              {outcomeLabel('Exclude:', 'the spend should not count, either because it is not emissions relevant (wages, VAT, depreciation) or because you already measure it from consumption data. Excluded lines stay visible with their reason.')}
              {outcomeLabel('Leave unmapped:', 'the code falls back to classification by supplier, as it does today. Mapping part of your chart is safe.')}
              <Typography variant='body2' color='text.secondary'>
                Mapping applies to future syncs and to lines still waiting in review. Emissions you have already
                committed are left alone.
              </Typography>
            </Stack>
          </Collapse>
      </Box>

      {isReviewScope && (
        <Alert
          severity='info'
          sx={{ mb: 2 }}
          action={returnParam === 'review' ? (
            <Button color='inherit' size='small' startIcon={<ChevronLeft fontSize='small' />} onClick={backToReviewHandler}>
              <Typography variant='body2' sx={{ pl: 0.5 }}>Back to review</Typography>
            </Button>
          ) : undefined}>
          Showing the codes on lines in review for this period. Saving applies to those lines straight away.
        </Alert>
      )}

      {refreshError && (
        <Alert severity='warning' sx={{ mb: 2 }} onClose={() => setRefreshError(null)}>
          {refreshError} {lastRead ? 'The chart last read is still listed below.' : 'The codes already seen on your spend are still listed below.'}
        </Alert>
      )}

      {loadError && <Alert severity='error' sx={{ mb: 2 }}>{loadError}</Alert>}

      {ledgerChoices && ledgerChoices.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            You have more than one accounting integration connected. Choose the one to read your chart of accounts from.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {ledgerChoices.map(choice => (
              <Box key={choice.code} sx={{ minWidth: { sm: 200 } }}>
                <SelectableCard
                  compact
                  title={choice.name}
                  selected={choice.code === lastRead?.provider}
                  disabled={isRefreshing}
                  onClick={() => refreshFromLedger(choice)} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {isEmpty ? (
        <InformationBox
          icon={IconName.AccountTree}
          text='Nothing to map yet'
          additionalText='Codes arrive from a connected ledger sync, a spend spreadsheet with a Nominal Code column, or Add code above.' />
      ) : (
        <NominalCodeMappingWorkspace
          mapping={mapping}
          reference={reference}
          isLoading={isLoading}
          hasSummary={!!summary}
          onDeleteMapping={deleteMappingHandler}
          onEditMapping={editMappingHandler}
        />
      )}

      {/* Sticky footer: what a save would change, and the only way to save it. */}
      {hasUnsaved && (
        <NominalCodeSaveBar
          counts={mapping.footerCounts}
          isSaving={mapping.isSaving}
          onSave={saveHandler}
          onDiscard={mapping.discardHandler}
          saveLabel='Save and apply'
        />
      )}

      {openMappingDialog && !editingMapping &&
        <NominalCodeMappingDialog
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openMappingDialog}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          isEdit={false} />}
      {openMappingDialog && editingMapping &&
        <NominalCodeMappingDialog
          isSubmitting={isDialogSubmitting}
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openMappingDialog}
          closeHandler={closeDialogHandler}
          isEdit={true}
          mappingId={editingMapping.id} />}
      {openImportDialog &&
        <NominalCodeImportDialog
          open={openImportDialog}
          closeHandler={() => setOpenImportDialog(false)}
          onImported={reloadSummary} />}
    </PageOuterContainer>
  )
}
